import React, { createContext, useCallback, useEffect, useState } from 'react';

import {
  BatchUploadResponse,
  BatchUploadResultItem,
  Device,
  Doc,
  Loadable,
  PresignedSlot,
} from '../types';
import { EVERYTHING } from '../constants/TimeConstants';
import { apiClient, getCacheControl } from '../api';
import { BatchUploadOptions, documentService } from '../api/services/documentService';
import { sha256OfFile } from '../utils/sha256';

interface RefreshOptions {
  silent?: boolean;
}

export interface DocumentsQuota {
  usedBytes: number;
  limitBytes: number;
}

// Files at or below this size go through the multipart batch endpoint;
// anything larger uses the presigned direct-to-S3 path. Matches
// server/config.js documents.batch.presignedThresholdBytes.
const PRESIGNED_THRESHOLD_BYTES = 25_000_000;

// Reasonable conservative fallback when we have to fabricate a quota
// summary for a pure-presigned upload (the presigned route returns the
// updated quota only via the next list refresh).
const DEFAULT_QUOTA_LIMIT = 209_715_200; // 200 MB

export interface SaveDocumentsOptions extends BatchUploadOptions {
  // Per-file progress hook for presigned uploads. `index` matches the
  // original `files` array index. Batch-path files emit a single 100%
  // event once the multipart POST resolves.
  onProgress?: (index: number, loadedBytes: number, totalBytes: number) => void;
}

interface DocumentContextType {
  device?: Device;
  documents?: Loadable<Doc>;
  documentsInterval: number | undefined;
  setDocumentsInterval: (interval: number | undefined) => void;
  saveDocument: (document: Doc, file: File) => Promise<void>;
  saveDocuments: (files: File[], opts?: SaveDocumentsOptions) => Promise<BatchUploadResponse>;
  removeDocument: (documentId: string) => Promise<void>;
  refreshDocuments: (options?: RefreshOptions) => Promise<void>;
  quota?: DocumentsQuota;
  refreshQuota: () => Promise<void>;
}

const loadableDocuments: Loadable<Doc> = { loading: true };

const initialContext: DocumentContextType = {
  documents: loadableDocuments,
  documentsInterval: EVERYTHING,
  setDocumentsInterval: () => {},
  saveDocument: () => Promise.resolve(),
  saveDocuments: () => Promise.reject(new Error('no device')),
  removeDocument: () => Promise.reject(new Error('no device')),
  refreshDocuments: () => Promise.resolve(),
  quota: undefined,
  refreshQuota: () => Promise.resolve(),
};

const DocumentContext = createContext<DocumentContextType>(initialContext);

interface DocumentProviderProps {
  device?: Device;
  children: React.ReactNode;
}

function DocumentProvider({ device, children }: DocumentProviderProps) {
  const [documents, setDocuments] = useState<Loadable<Doc> | undefined>();
  const [documentsInterval, setDocumentsInterval] = useState<number | undefined>(EVERYTHING);
  const [quota, setQuota] = useState<DocumentsQuota | undefined>();

  // Single source of truth for the GET. Bypasses the in-memory GET cache so
  // we always see fresh server state — important after another caregiver
  // uploads or removes a document while the sidebar is open.
  const refreshDocuments = useCallback(
    async ({ silent = false }: RefreshOptions = {}) => {
      if (!device) return;
      if (!silent) setDocuments({ loading: true });
      const from = documentsInterval ? new Date(Date.now() - documentsInterval) : undefined;
      try {
        getCacheControl(apiClient).clear(`/api/devices/${device.id}/documents`);
        const response = await documentService.getDocuments(device.id, from);
        const data = response.data.map((a: Doc) => ({ ...a, createdAt: new Date(a.createdAt) }));
        setDocuments({ loading: false, data });
      } catch (err) {
        setDocuments((prev) => ({ loading: false, error: err as Error, data: prev?.data }));
      }
    },
    [device, documentsInterval],
  );

  const refreshQuota = useCallback(async () => {
    if (!device) return;
    try {
      getCacheControl(apiClient).clear(`/api/devices/${device.id}/documents/quota`);
      const response = await documentService.getDocumentsQuota(device.id);
      setQuota(response.data);
    } catch {
      // Silent on failure — quota is a hint, not load-bearing for upload.
      setQuota(undefined);
    }
  }, [device]);

  useEffect(() => {
    refreshDocuments();
    refreshQuota();
  }, [refreshDocuments, refreshQuota]);

  const saveDocument = async (document: Doc, file: File): Promise<void> => {
    if (!device) return Promise.reject('no device');

    try {
      const response = await documentService.uploadDocument(device.id, document, file);
      const update: Doc = { ...response.data, createdAt: new Date(response.data.createdAt) };
      const data: Doc[] = [...(documents?.data ?? []), update];
      setDocuments({ loading: false, error: undefined, data });
      refreshDocuments({ silent: true });
      refreshQuota();
    } catch (err) {
      setDocuments({ loading: false, error: err as Error });
      throw err;
    }
  };

  // Mixed upload: routes each file based on size. ≤ 25 MB → multipart
  // batch endpoint (cheap single round-trip, server dedupes by sha256).
  // > 25 MB → presigned direct-to-S3 (one request per upload but no
  // proxying through Node, plus per-file progress events).
  // Both paths' results are merged into one BatchUploadResponse so the
  // caller (BatchUploadModal) doesn't care which path each file took.
  const saveDocuments = async (
    files: File[],
    opts: SaveDocumentsOptions = {},
  ): Promise<BatchUploadResponse> => {
    if (!device) return Promise.reject(new Error('no device'));
    const deviceId = device.id;

    const indexed = files.map((file, originalIndex) => ({ file, originalIndex }));
    const small = indexed.filter(({ file }) => file.size <= PRESIGNED_THRESHOLD_BYTES);
    const large = indexed.filter(({ file }) => file.size > PRESIGNED_THRESHOLD_BYTES);

    const results: (BatchUploadResultItem | undefined)[] = new Array(files.length);
    let aggregatedQuota: BatchUploadResponse['summary']['quota'] | undefined;

    // ----- Batch path (≤ 25 MB) -----
    const batchPromise = (async () => {
      if (small.length === 0) return;
      const subLabels = small.map(({ originalIndex }) => opts.labels?.[originalIndex] ?? '');
      const subDescriptions = small.map(
        ({ originalIndex }) => opts.descriptions?.[originalIndex] ?? '',
      );
      const response = await documentService.uploadDocuments(deviceId, small.map((x) => x.file), {
        labels: subLabels,
        descriptions: subDescriptions,
      });
      aggregatedQuota = response.data.summary.quota;
      response.data.results.forEach((item, subIndex) => {
        const original = small[subIndex].originalIndex;
        results[original] = { ...item, index: original } as BatchUploadResultItem;
        opts.onProgress?.(original, small[subIndex].file.size, small[subIndex].file.size);
      });
    })();

    // ----- Presigned path (> 25 MB) -----
    const presignedPromise = (async () => {
      if (large.length === 0) return;
      // Hash all large files in parallel — client-side sha256 lets the
      // server store the digest on the eventual Document for future
      // dedup, without trusting client-computed quota numbers.
      const shas = await Promise.all(large.map(({ file }) => sha256OfFile(file)));

      // Single round-trip for slot issuance so server validates quota
      // across all large files atomically.
      let slots: PresignedSlot[];
      try {
        const issue = await documentService.requestPresignedUploads(
          deviceId,
          large.map(({ file }, k) => ({
            filename: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            sha256: shas[k],
          })),
        );
        slots = issue.data.slots;
      } catch (err: any) {
        const msg = err?.response?.data?.error?.[0]?.msg || 'presigned_request_failed';
        large.forEach(({ file, originalIndex }) => {
          results[originalIndex] = {
            status: 'rejected',
            index: originalIndex,
            filename: file.name,
            error: { code: msg },
          };
        });
        return;
      }

      // Upload + finalize each large file in parallel. Failures on any
      // individual slot become a per-file rejected result so the UI can
      // still report success for the rest of the batch.
      await Promise.all(
        large.map(async ({ file, originalIndex }, k) => {
          const slot = slots[k];
          try {
            await documentService.uploadToSlot(slot, file, (loaded, total) => {
              opts.onProgress?.(originalIndex, loaded, total);
            });
            const final = await documentService.finalizeUpload(deviceId, slot.slotId, {
              label: opts.labels?.[originalIndex],
              description: opts.descriptions?.[originalIndex],
            });
            results[originalIndex] = {
              status: 'created',
              index: originalIndex,
              document: final.data,
            };
          } catch (err: any) {
            const msg =
              err?.response?.data?.error?.[0]?.msg ||
              (err?.message?.startsWith('Network') ? 's3_upload_failed' : 'finalize_failed');
            results[originalIndex] = {
              status: 'rejected',
              index: originalIndex,
              filename: file.name,
              error: { code: msg },
            };
          }
        }),
      );
    })();

    await Promise.all([batchPromise, presignedPromise]);

    const filled = results.map((r, i) =>
      r ?? {
        status: 'rejected' as const,
        index: i,
        filename: files[i].name,
        error: { code: 'unknown_error' },
      },
    );

    const created: Doc[] = filled
      .filter((r): r is BatchUploadResultItem & { status: 'created' } => r.status === 'created')
      .map((r) => ({ ...r.document, createdAt: new Date(r.document.createdAt) }));
    if (created.length > 0) {
      const data: Doc[] = [...(documents?.data ?? []), ...created];
      setDocuments({ loading: false, error: undefined, data });
    }
    refreshDocuments({ silent: true });
    refreshQuota();

    const summary: BatchUploadResponse['summary'] = {
      total: files.length,
      created: filled.filter((r) => r.status === 'created').length,
      duplicate: filled.filter((r) => r.status === 'duplicate').length,
      rejected: filled.filter((r) => r.status === 'rejected').length,
      quota: aggregatedQuota ?? { usedBytes: 0, limitBytes: DEFAULT_QUOTA_LIMIT },
    };

    return { results: filled, summary };
  };

  const removeDocument = async (documentId: string): Promise<void> => {
    if (!device) return Promise.reject(new Error('no device'));

    await documentService.deleteDocument(device.id, documentId);
    const data = (documents?.data ?? []).filter((d) => d.id !== documentId);
    setDocuments({ loading: false, error: undefined, data });
    refreshDocuments({ silent: true });
    refreshQuota();
  };

  return (
    <DocumentContext.Provider
      value={{
        device,
        documents,
        documentsInterval,
        setDocumentsInterval,
        saveDocument,
        saveDocuments,
        removeDocument,
        refreshDocuments,
        quota,
        refreshQuota,
      }}
    >
      {children}
    </DocumentContext.Provider>
  );
}

export { DocumentContext, DocumentProvider };
